<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Die 360°-Fotografie hat die Immobilienbranche grundlegend verändert.
            Immobilien mit Virtual Tours erhalten bis zu 87 % mehr Anfragen und
            verkaufen sich doppelt so schnell wie Objekte ohne digitale
            Präsentation. Dieser umfassende Leitfaden zeigt, wie Sie
            professionelle 360°-Touren für Immobilien erstellen.
          </p>
        </section>

        <section>
          <h2>Warum 360°-Fotografie für Immobilien unverzichtbar ist</h2>
          <p>
            Traditionelle Immobilienfotos sind selektiv und statisch – sie
            zeigen nur das, was der Fotograf einrahmt. 360°-Virtual-Tours sind
            transparent und immersiv: Interessenten erkunden Räume selbst,
            entwickeln ein intuitives Raumverständnis und bauen Vertrauen auf,
            bevor sie die Schwelle übertreten.
          </p>
          <p>
            Besonders für Kaufinteressenten aus anderen Städten oder im Ausland
            sind Virtual Tours unersetzlich: Die Besichtigung kann digital
            stattfinden, der physische Termin dient nur noch zur Bestätigung.
            Das beschleunigt Entscheidungsprozesse erheblich.
          </p>
        </section>

        <section>
          <h2>Ausrüstungsoptionen für 360°-Immobilienfotografie</h2>
          <p>
            <strong>Smartphone (günstig &amp; effizient):</strong> Moderne
            Smartphones mit 360°-Apps wie Travvir liefern ausgezeichnete
            Ergebnisse für die meisten Wohnimmobilien. Ideal für Makler, die
            häufig Listings erstellen und Geschwindigkeit priorisieren.
          </p>
          <p>
            <strong>Ricoh Theta Z1 (ca. 750 €):</strong> Kompakte dedizierte
            360°-Kamera mit hervorragender Qualität. Einstecken und direkt
            fotografieren – kein komplexes Stitching erforderlich.
            Empfehlenswert für professionelle Makler mit mittlerem Volumen.
          </p>
          <p>
            <strong>Insta360 X4 (ca. 449 €):</strong> Aktueller Markführer im
            Consumer-Segment. Ausgezeichnete Bildqualität, gute
            Low-Light-Performance und intuitive App-Integration. Hervorragendes
            Preis-Leistungs-Verhältnis.
          </p>
          <p>
            <strong>Matterport Pro3 (ab 5.400 €):</strong> Professionelles
            Lidar-basiertes 3D-Scannersystem für Premiumobjekte. Liefert
            messbare Grundrisse und Puppenhaus-Ansichten. Nur bei Luxusobjekten
            und gewerblichen Immobilien wirtschaftlich.
          </p>
          <p>
            <strong>Wichtiges Zubehör:</strong> Stabiles Stativ mit
            Kugelkopf-Level, externes LED-Licht für dunkle Räume, Wasserwaage,
            Reinigungsset für Objektive.
          </p>
        </section>

        <section>
          <h2>Vorbereitung vor der Aufnahme</h2>
          <p>
            Informieren Sie den Eigentümer oder Mieter rechtzeitig über die
            Anforderungen: Räume müssen professionell gestaged sein, alle
            persönlichen Gegenstände entfernt, alle Lichter funktionsfähig.
          </p>
          <p>
            Planen Sie mindestens 30 Minuten Vorbereitung ein, bevor Sie mit dem
            Fotografieren beginnen. Gehen Sie jeden Raum durch und prüfen Sie:
            Lichtsituation, Sauberkeit, Positionierung von Möbeln, störende
            Elemente im Bildbereich. Öffnen Sie alle Türen zu Nebenräumen, um
            Raumtiefe zu zeigen. Schalten Sie alle Decken- und Spotlichter ein;
            deaktivieren Sie automatische Rollos oder Jalousien für die
            Aufnahme.
          </p>
        </section>

        <section>
          <h2>Zimmer-für-Zimmer-Strategie</h2>
          <p>
            <strong>Wohnzimmer:</strong> Zentrales Aufnahmepunkt in der
            Raummitte, gleichweit von allen Wänden. Zeigen Sie Kamin,
            Panoramafenster und Hauptmöbelgruppe. 1–2 Aufnahmen je nach
            Raumgröße.
          </p>
          <p>
            <strong>Küche:</strong> Positionierung für Blick auf Kücheninsel,
            Arbeitsbereich und Geräte. Räumen Sie vollständig alle
            Arbeitsflächen frei. In offenen Grundrissen: separate Aufnahme der
            Kochzone und des Esstisches.
          </p>
          <p>
            <strong>Schlafzimmer:</strong> Fußbodenseite des Bettes, um maximale
            Raumtiefe zu zeigen. Kleiderschranktüren angelehnt öffnen.
            Fenstervorhänge geordnet und symmetrisch. Premium-Bettwäsche frisch
            gewaschen und professionell arrangiert.
          </p>
          <p>
            <strong>Badezimmer:</strong> Schmal, aber wichtig – Aufnahme von der
            Türseite. Frottiertücher hochwertig gefaltet auslegen.
            Toilettendeckel schließen. Alle Hygiene-Produkte verstecken.
          </p>
          <p>
            <strong>Außenbereich:</strong> Aufnahmen am frühen Morgen oder
            späten Nachmittag für bestes Licht. Zeigen Sie Garten, Terrasse,
            Pool, Einfahrt. Grünflächen vorher mähen und aufräumen.
          </p>
        </section>

        <section>
          <h2>Technische Einstellungen</h2>
          <p>
            Verwenden Sie immer die maximale verfügbare Auflösung. Deaktivieren
            Sie HDR für konsistente Stitching-Ergebnisse. Sperren Sie die
            Belichtung auf einen mittleren Tonwert im Raum. ISO so niedrig wie
            möglich halten (ISO 100–400) für rauschfreie Bilder. Weißabgleich
            manuell auf „Kunstlicht" (Innen) oder „Bewölkt" (Außen) setzen.
          </p>
          <p>
            Stativhöhe: 130–140 cm für Wohnräume und Küchen; 110–120 cm für
            Badezimmer (zur optimalen Darstellung des relativ kleinen Raumes).
          </p>
        </section>

        <section>
          <h2>Verarbeitung und Veröffentlichung</h2>
          <p>
            Laden Sie Ihre Aufnahmen direkt in die Travvir-App hoch. Die KI
            übernimmt automatisch das Stitching und die Belichtungskorrektur.
            Fügen Sie Hotspots für Navigation zwischen Räumen hinzu. Geben Sie
            jedem Hotspot einen aussagekräftigen Namen (z. B. „Küche",
            „Schlafzimmer 1"). Fügen Sie auf Anfrage Immobiliendaten als
            Hotspot-Labels hinzu: Raumgröße, Ausstattungsmerkmale.
          </p>
          <p>
            Veröffentlichen Sie die Tour und teilen Sie den Link in Ihrem
            MLS-Listing, auf Ihrer Website, in sozialen Medien und in der
            E-Mail-Kommunikation mit Interessenten.
          </p>
        </section>

        <section>
          <h2>Häufige Fehler bei der Immobilienfotografie</h2>
          <p>
            <strong>Fehler 1: Mangelhafte Raumvorbereitung.</strong> Nehmen Sie
            sich die Zeit, jeden Raum professionell zu stagen. Unordnung in
            Virtual Tours ist weitaus auffälliger als in statischen Fotos.
          </p>
          <p>
            <strong>Fehler 2: Falsche Aufnahmeposition.</strong> Immer aus der
            Raummitte fotografieren, nie aus Ecken oder Türöffnungen.
          </p>
          <p>
            <strong>Fehler 3: Gemischte Lichtquellen.</strong> Schalten Sie
            entweder alle künstlichen Lichter ein (für dunkle Immobilien) oder
            öffnen Sie alle Fenster. Niemals beides mischen, da dies Farbstiche
            erzeugt.
          </p>
          <p>
            <strong>Fehler 4: Eigenes Spiegelbild vergessen.</strong> Prüfen Sie
            jeden Raum auf Spiegel, hochglänzende Oberflächen und reflektierende
            Böden. In diesen könnten Sie selbst sichtbar sein.
          </p>
          <p>
            <strong>Fehler 5: Tour nicht testen vor Veröffentlichung.</strong>
            Navigieren Sie jede Tour komplett selbst, bevor Sie sie an Kunden
            senden. Überprüfen Sie alle Hotspots auf korrekte Verlinkung.
          </p>
        </section>

        <section>
          <h2>Fazit</h2>
          <p>
            360°-Virtual-Tours sind heute ein Standard-Tool für professionelle
            Immobilienmakler. Mit dem richtigen Equipment, einer strukturierten
            Vorbereitung und der Travvir-App können Sie in weniger als einer
            Stunde Touren erstellen, die Ihre Listings deutlich von der
            Konkurrenz abheben. Beginnen Sie noch heute – Ihre Listings, Ihre
            Kunden und Ihre Verkaufsrate werden es Ihnen danken.
          </p>
        </section>

        
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/virtual-tour-immobilien-erstellen">Virtual Tour Immobilien erstellen : Anleitung</a></li>
            <li><a href="/blog/de/innenraum-fotografie-360-virtual-tour">Innenraum-Fotografie & 360° Virtual Tour</a></li>
            <li><a href="/blog/de/360-fotos-mit-smartphone-aufnehmen">360°-Fotos mit Smartphone : Vollständige Anleitung</a></li>
            <li><a href="/blog/de/unscharfe-360-fotos-beheben">Unscharfe 360°-Fotos beheben : Ursachen & Lösungen</a></li>
            <li><a href="/blog/de/360-kamera-vs-dslr-immobilien">360 Kamera vs. DSLR Immobilien : Was wählen?</a></li>
            <li><a href="/blog/de/immobilien-virtual-tour-smartphone">Immobilien Virtual Tour Smartphone</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "360-immobilienfotografie-leitfaden",
);

useBlogSeo({
  title: "360°-Immobilienfotografie [2026]: Leitfaden für Makler | Travvir",
  description:
    "Vollständiger Leitfaden zur 360°-Immobilienfotografie. Kamera-Wahl, Aufnahmetechnik, Bearbeitung und Veröffentlichung auf Immobilienportalen.",
  slug: "360-immobilienfotografie-leitfaden",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
